import styled, { css } from 'styled-components';
import { Tooltip, IconV2, type WithTooltipProps } from '@abtasty/design-system';
import { TestSubType } from '@src/application/modules/tag/shared/tag/serializedTagTypes';
import { QaTestType } from '@src/application/modules/tag/shared/tag/types';
import classNames from 'classnames';
import { ComponentProps, FC, VFC } from 'react';
import { useTranslation } from 'react-i18next';

type TestIconSubTypeProps = {
  testSubType?: TestSubType;
};
const TestIconSubType: VFC<TestIconSubTypeProps> = ({ testSubType }) => {
  const { t } = useTranslation();

  switch (testSubType) {
    case TestSubType.patch:
      return <IconCampaign icon="patch" tooltip={{ content: t('Patch'), placement: 'top' }} />;
    case TestSubType.multipagePersonalization:
      return (
        <IconCampaign icon="persoMultipages" tooltip={{ content: t('Multipage Personalization'), placement: 'top' }} />
      );
    case TestSubType.multiexperiencePersonalization:
      return (
        <IconCampaign
          icon="persoMultiexperience"
          tooltip={{ content: t('Multiexperience Personalization'), placement: 'top' }}
        />
      );
    case TestSubType.simplePersonalization:
    default:
      return (
        <IconCampaign icon="personalization" tooltip={{ content: t('Simple Personalization'), placement: 'top' }} />
      );
  }
};

export type TestIconProps = {
  testType: QaTestType;
  testSubType?: TestSubType;
};

const TestIconInner: VFC<TestIconProps> = ({ testType, testSubType }) => {
  const { t } = useTranslation();
  switch (testType) {
    case QaTestType.multipage:
      if (testSubType === TestSubType.patch)
        return (
          <IconCampaign
            icon="multipagePatch"
            tooltip={{ content: t('Multipage Patch', { context: 'multipagePatch' }), placement: 'top' }}
          />
        );
      return <IconCampaign icon="mPTest" tooltip={{ content: t('Multipage Test'), placement: 'top' }} />;
    case QaTestType.multivariate:
      return <IconCampaign icon="mVTest" tooltip={{ content: t('Multivariate Test'), placement: 'top' }} />;
    case QaTestType.predictive:
      return <IconCampaign icon="aBTestDynamic" tooltip={{ content: t('Dynamic AB Test'), placement: 'top' }} />;
    case QaTestType.personalization:
      return <TestIconSubType testSubType={testSubType} />;
    case QaTestType.aa:
      return (
        <IconCampaign icon="aATest" tooltip={{ content: t('AA Test', { context: 'aaTest' }), placement: 'top' }} />
      );
    case QaTestType.ab:
      return testSubType && testSubType !== TestSubType.ab ? (
        <TestIconSubType testSubType={testSubType} />
      ) : (
        <IconCampaign icon="aBTest" tooltip={{ content: t('AB Test'), placement: 'top' }} />
      );
    default:
      return <IconCampaign icon="aBTest" tooltip={{ content: t('AB Test'), placement: 'top' }} />;
  }
};

const ThemeWrapper = styled.div`
  ${({ theme }) => css`
    svg {
      width: 24px;
      height: 24px;
      path {
        fill: ${theme.dec.color.primary.default.fg.brand};
      }
    }
  `}
`;

export const TestIcon: VFC<TestIconProps> = (props: TestIconProps) => (
  <ThemeWrapper>
    <TestIconInner {...props} />
  </ThemeWrapper>
);

const IconCampaignComponent: FC<ComponentProps<typeof IconV2> & { currentColor?: boolean }> = ({
  icon,
  currentColor = false,
  color,
  ...props
}) => {
  const iconClassNames = classNames(props.className, currentColor && !color && currentColor, {
    color,
  });

  return (
    <IconV2 icon={icon} {...props} className={iconClassNames} data-testid={props['data-testid'] || `${icon}Icon`} />
  );
};

const IconCampaign: FC<ComponentProps<typeof IconV2> & WithTooltipProps> = ({ tooltipType = 'small', ...props }) => (
  <Tooltip className="tooltip" type={tooltipType}>
    <IconCampaignComponent {...props} />
  </Tooltip>
);
